/* eslint-disable max-classes-per-file */
import AwsS3 from "@uppy/aws-s3";
import Uppy, { type UppyFile, type Meta } from "@uppy/core";
import { type Restrictions as UppyRestrictions } from "@uppy/core/lib/Restricter";
import { fromEvent } from "file-selector";

import { compressRasterImageForUpload } from "./compress-image-for-upload";
import {
  FileSizeError,
  getRestrictionsFailedError,
  LeadingUnderscoreFilenameError,
  type RestrictionsFailedError,
} from "./files-uploader-errors";

interface TransformationItem {
  crop?: "limit" | "fit";
  flags?: "relative";
  height?: number;
  width?: number;
  overlay?: "deco_watermark" | "canvas_overlay_sml";
  opacity?: number;
}

/**
 * @description this are cloudinary transformations options. Values are exact based on code usage in the project, and does not include all possible options provided by cloudinary(which may not be always covered in code).
 *
 * You can call these transformations once file is uploaded, this can be done e.g in the 'setupUploadSuccessHandler' inside uppy "upload-success" event handler.
 */
export interface Options {
  /** @description see https://cloudinary.com/documentation/resizing_and_cropping#resize_and_crop_modes */
  crop?: "limit" | "fit" | "scale" | "mpad";
  width?: number;
  height?: number;
  /** @description see https://cloudinary.com/documentation/upload_parameters#the_39_auto_39_resource_type */
  resourceType?: "auto";
  imageMetadata?: boolean;
  useFilename?: boolean;
  exif?: boolean;
  /** @description see https://cloudinary.com/documentation/transformation_reference#fl_keep_iptc as example */
  flags?: "keep_iptc" | "relative" | "no_overflow";
  /** @description see https://cloudinary.com/documentation/eager_and_incoming_transformations */
  eagerAsync?: boolean;
  /** @description see https://cloudinary.com/documentation/eager_and_incoming_transformations */
  eager?: {
    /** @description looks like only jpg is used, at least for uploading */
    format?: "jpg";
    transformation: TransformationItem[];
  }[];
}

export interface Restrictions {
  maxFileSize?: UppyRestrictions["maxFileSize"];
  minFileSize?: UppyRestrictions["minFileSize"];
  maxNumberOfFiles?: UppyRestrictions["maxNumberOfFiles"];
  /**
   * @description The mime types of the files that are allowed to be uploaded
   * @example ["image/webp", "image/jpeg", "image/png", "image/gif"]
   */
  allowedFileTypes?: UppyRestrictions["allowedFileTypes"];
}

interface FilesUploaderOptions {
  restrictions?: Restrictions;
  options?: Options;
  onRestrictionsFailed?: (
    error: RestrictionsFailedError,
    restrictions: Restrictions
  ) => void;
  /**
   * @description Fired each time an individual file upload progress is available
   * @param percent - The percentage of the upload progress
   */
  onUploadProgress?: (filename: string, percent: number) => void;
  onUploadFailed?: (filename: string) => void;
  onUploadStart?: () => void;
  /**
   * @description Fired each time a single upload is completed.
   */
  onUploadSuccess?: (params: {
    fileName: string;
    uploadUrl: string;
    folderName: string | null;
    fileExtension: string;
    publicId: string;
    file: File;
  }) => void;
  /**
   * @description Fired when all uploads are completed.
   */
  onComplete?: () => void;
  /**
   * @description Fired before a file is added to the uploader.
   */
  onBeforeAddFiles?: (() => void) | undefined;
}

type ExtendedFileMeta = Meta | (Meta & CustomMetaFields);

interface CustomMetaFields {
  relativePath: string;
  path: string;
  isFolder: boolean;
}

const fetchSignedUrl = async ({
  fileName,
  fileSize,
  fileType,
}: {
  fileName: string | undefined;
  fileType: string;
  fileSize: number | null;
}) =>
  fetch("/uploads/s3/fetch_signed_url", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      file_name: fileName,
      file_type: fileType,
      file_size: fileSize,
    }),
  })
    .then(
      (res) =>
        res.json() as Promise<{
          url: string;
        } | null>
    )
    .then((data) => {
      if (!data) throw new Error("Failed to generate signed URL");

      return data;
    })
    .catch((error) => {
      console.error("Failed to generate signed URL:", error);
      throw error;
    });

export class FilesUploader {
  private readonly uppyInstance: Uppy;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- this will be potentially used e.g to trigger transformations
  // @ts-ignore
  private readonly options: Options | undefined;

  private static readonly DEFAULT_ALLOWED_FILE_MIME_TYPES = [
    "image/webp",
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/tiff",
    "image/heic",
    "image/heif",
    "application/pdf",
    "image/bmp",
    "image/x-icon",
    // Adobe Photoshop files, .photoshop
    "image/vnd.adobe.photoshop",
    "application/x-photoshop",
    "application/photoshop",
    "application/psd",
    "image/psd",
    // PostScript files, .ps
    "application/postscript",
    // Encapsulated PostScript files
    "application/eps",
    "application/x-eps",
    "image/eps",
    "image/x-eps",
    // rest
    "image/svg+xml",
    "image/jp2",
    "image/jpc",
    "image/j2k",
    "image/wdp",
    "image/jxr",
    "image/hdp",
    "image/flif",
    "image/djvu",
    "image/vnd.djvu",
    "image/x-djvu",
    "image/vnd.djvu+multipage",
    // video
    "video/mp4",
  ];

  private static readonly DEFAULT_MAX_FILE_SIZE = 50000000; // 50MB

  private static readonly DEFAULT_MIN_FILE_SIZE = 1000; // 1KB

  private static readonly DEFAULT_MAX_NUMBER_OF_FILES = 20;

  private readonly onBeforeAddFiles: (() => void) | undefined;

  private readonly onRestrictionsFailedCallback:
    | ((error: RestrictionsFailedError, restrictions: Restrictions) => void)
    | undefined;

  constructor({
    restrictions,
    onRestrictionsFailed,
    onUploadProgress,
    onUploadFailed,
    onUploadStart,
    onUploadSuccess,
    onComplete,
    onBeforeAddFiles,
    options,
  }: FilesUploaderOptions) {
    this.options = options;
    this.onRestrictionsFailedCallback = onRestrictionsFailed;

    const { maxFileSize, minFileSize, maxNumberOfFiles, allowedFileTypes } =
      restrictions ?? {};

    this.uppyInstance = new Uppy({
      autoProceed: false,
      restrictions: {
        allowedFileTypes:
          allowedFileTypes ?? FilesUploader.DEFAULT_ALLOWED_FILE_MIME_TYPES,
        maxFileSize: maxFileSize ?? FilesUploader.DEFAULT_MAX_FILE_SIZE,
        minFileSize: minFileSize ?? FilesUploader.DEFAULT_MIN_FILE_SIZE,
        maxNumberOfFiles:
          maxNumberOfFiles ?? FilesUploader.DEFAULT_MAX_NUMBER_OF_FILES,
      },
    });

    this.setupAwsS3Upload();
    this.setupRestrictionsFailedHandler(onRestrictionsFailed);
    this.setupUploadStartHandler(onUploadStart);
    this.setupUploadProgressHandler(onUploadProgress);
    this.setupUploadFailedHandler(onUploadFailed);
    this.setupUploadSuccessHandler(onUploadSuccess);
    this.setupCompleteHandler(onComplete);

    this.onBeforeAddFiles = onBeforeAddFiles;
  }

  public allowedFileExtensions() {
    const { allowedFileTypes } = this.uppyInstance.opts.restrictions;

    if (!allowedFileTypes) throw new Error("No allowed file types");

    return allowedFileTypes;
  }

  private static getFolderNameFromPath(path: string): string {
    const pathParts = path.split("/");

    if (pathParts.length > 4)
      throw new Error("Nested folders are not supported");

    const folderName = pathParts[1];

    if (!folderName) throw new Error("Folder name not found");

    return `${folderName}/`;
  }

  private static getPublicId(uploadUrl: URL): string {
    const pathName = uploadUrl.pathname;
    const pathParts = pathName.split("/");

    const publicId = pathParts[pathParts.length - 2];

    if (!publicId) throw new Error("Public ID not found");

    return publicId;
  }

  private setupAwsS3Upload() {
    this.uppyInstance.use(AwsS3, {
      shouldUseMultipart: false,
      getUploadParameters: async (file) => {
        const { url } = await fetchSignedUrl({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        });

        return {
          method: "PUT",
          url,
          fields: {},
          headers: {
            "Content-Type": file.type,
          },
        };
      },
    });
  }

  private setupRestrictionsFailedHandler(
    onRestrictionsFailed?: (
      error: RestrictionsFailedError,
      restrictions: Restrictions
    ) => void
  ) {
    this.uppyInstance.on("restriction-failed", (_file, error) => {
      const customError = getRestrictionsFailedError(error);

      if (onRestrictionsFailed) {
        onRestrictionsFailed(
          customError,
          this.uppyInstance.opts.restrictions as Restrictions
        );
      }

      throw customError;
    });
  }

  private setupUploadStartHandler(onUploadStart?: () => void) {
    if (!onUploadStart) return;

    this.uppyInstance.on("upload-start", onUploadStart);
  }

  private setupUploadProgressHandler(
    onUploadProgress?: (filename: string, percent: number) => void
  ) {
    if (!onUploadProgress) return;

    this.uppyInstance.on("upload-progress", (file, progress) => {
      if (!file) throw new Error("File not found");
      if (!progress.bytesTotal) return;

      const percent = Math.floor(
        (progress.bytesUploaded / progress.bytesTotal) * 100
      );

      onUploadProgress(file.name ?? "", percent);
    });
  }

  private setupUploadFailedHandler(
    onUploadFailed?: (filename: string) => void
  ) {
    if (!onUploadFailed) return;

    this.uppyInstance.on("upload-error", (file) => {
      if (!file) throw new Error("File not found");

      onUploadFailed(file.name ?? "");
    });
  }

  private setupUploadSuccessHandler(
    onUploadComplete?: (params: {
      fileName: string;
      uploadUrl: string;
      folderName: string | null;
      fileExtension: string;
      publicId: string;
      file: File;
    }) => void
  ) {
    if (!onUploadComplete) return;

    this.uppyInstance.on("upload-success", (uppyFile, response) => {
      const file = uppyFile as UppyFile<
        ExtendedFileMeta,
        Record<string, never>
      >;

      if (!file) throw new Error("File not found");
      if (!response.uploadURL) throw new Error("Upload URL not found");

      const uploadUrl = new URL(response.uploadURL);

      const folderName =
        "isFolder" in file.meta && file.meta.isFolder
          ? FilesUploader.getFolderNameFromPath(
              file.meta.relativePath as CustomMetaFields["relativePath"]
            )
          : null;

      if (!file.name) throw new Error("File name not found");

      const uploadFile = file.data;

      if (!(uploadFile instanceof Blob)) {
        throw new Error(`Upload file data missing for ${file.name}`);
      }

      onUploadComplete({
        fileName: file.name,
        uploadUrl: uploadUrl.pathname,
        folderName,
        fileExtension: file.extension,
        publicId: FilesUploader.getPublicId(uploadUrl),
        file: uploadFile as File,
      });
    });
  }

  private setupCompleteHandler(onComplete?: () => void) {
    if (!onComplete) return;

    this.uppyInstance.on("complete", onComplete);
  }

  public cancelAll() {
    this.uppyInstance.cancelAll();
  }

  /**
   * The backend pipeline does not support file names starting with an
   * underscore — such uploads silently stall (ART-8339). Reject them at the
   * UI layer with a clear message before any compression / S3 request.
   */
  private static hasLeadingUnderscore(name: string | undefined): boolean {
    if (!name) return false;

    // Strip any directory prefix the browser might attach (e.g. drag/drop
    // from a folder gives "subdir/_file.png").
    const basename = name.split("/").pop() ?? name;

    return basename.startsWith("_");
  }

  /**
   * Filter out files whose name starts with an underscore, reporting each
   * via `onRestrictionsFailed`. Returns the surviving list.
   */
  private rejectLeadingUnderscoreFiles<T extends File>(files: T[]): T[] {
    const surviving: T[] = [];

    for (const file of files) {
      if (FilesUploader.hasLeadingUnderscore(file.name)) {
        if (this.onRestrictionsFailedCallback) {
          this.onRestrictionsFailedCallback(
            new LeadingUnderscoreFilenameError(),
            this.uppyInstance.opts.restrictions as Restrictions
          );
        }
      } else {
        surviving.push(file);
      }
    }

    return surviving;
  }

  /**
   * Hard size gate on the RAW file, before any preprocessing/compression: any file
   * whose original size exceeds the configured maxFileSize is blocked outright and
   * reported via `onRestrictionsFailed` (FileSizeError) — no compression attempt
   * (ART-8656). Runs per file, so a multi-file selection drops only the oversized
   * file(s) and uploads the rest. Returns the surviving (within-cap) list.
   */
  private rejectOversizeFiles<T extends File>(files: T[]): T[] {
    const { maxFileSize } = this.uppyInstance.opts.restrictions;

    if (typeof maxFileSize !== "number") {
      return files;
    }

    const surviving: T[] = [];

    for (const file of files) {
      if (file.size > maxFileSize) {
        if (this.onRestrictionsFailedCallback) {
          this.onRestrictionsFailedCallback(
            new FileSizeError(),
            this.uppyInstance.opts.restrictions as Restrictions
          );
        }
      } else {
        surviving.push(file);
      }
    }

    return surviving;
  }

  public async addFilesFromInput(fileList: FileList) {
    if (this.onBeforeAddFiles) {
      this.onBeforeAddFiles();
    }

    const validFiles = this.rejectOversizeFiles(
      this.rejectLeadingUnderscoreFiles(Array.from(fileList))
    );

    if (validFiles.length === 0) {
      return [];
    }

    const files = await Promise.all(
      validFiles.map(async (file) => {
        const data = await compressRasterImageForUpload(file);

        return {
          source: "file input",
          name: data.name,
          type: data.type,
          data,
        };
      })
    );

    return this.uppyInstance.addFiles(files);
  }

  public async addFilesFromDragEvent(event: Event) {
    const files = await fromEvent(event);

    // Reject leading-underscore filenames (ART-8339) and over-cap files (ART-8656)
    // up front, on the RAW file before any preprocessing/compression. We surface
    // both through onRestrictionsFailed (same UX as the input path) and keep going
    // with the survivors, so a multi-file drop drops only the offending file(s).
    const { maxFileSize } = this.uppyInstance.opts.restrictions;
    const validEntries = Array.from(files).filter((file) => {
      if (file instanceof DataTransferItem) return true; // handled below

      if (FilesUploader.hasLeadingUnderscore(file.name)) {
        if (this.onRestrictionsFailedCallback) {
          this.onRestrictionsFailedCallback(
            new LeadingUnderscoreFilenameError(),
            this.uppyInstance.opts.restrictions as Restrictions
          );
        }

        return false;
      }

      if (typeof maxFileSize === "number" && file.size > maxFileSize) {
        if (this.onRestrictionsFailedCallback) {
          this.onRestrictionsFailedCallback(
            new FileSizeError(),
            this.uppyInstance.opts.restrictions as Restrictions
          );
        }

        return false;
      }

      return true;
    });

    if (validEntries.length === 0) {
      return [];
    }

    const filesWithMeta = await Promise.all(
      validEntries.map(async (file) => {
        if (file instanceof DataTransferItem)
          throw new Error("Not implemented");

        // `"handle" in file` used to gate this: file-selector only attaches
        // `.handle` when `DataTransferItem.getAsFileSystemHandle()` succeeds, an
        // experimental Chromium-only API. Firefox/Safari never implement it and
        // Brave removes it, so `.handle` was always absent there and every
        // single-file drop was misread as a folder (ART-8444). relativePath is
        // always populated, so folder-ness is determined by path depth instead —
        // but file-selector's own fallback for a lone file in a secure context
        // (the Chrome/handle path) stamps relativePath as "./name" with NO
        // directory info, so a bare "." segment must be discarded too, or a
        // single file in Chrome counts as 2 segments and gets misread as a
        // folder instead (ART-8444-2).
        const pathSegments = (file.relativePath ?? "")
          .split("/")
          .filter((segment) => segment.length > 0 && segment !== ".");
        const isFolder = pathSegments.length > 1;

        const meta: ExtendedFileMeta = {
          isFolder,
          relativePath: file.relativePath,
          path: file.path,
        };

        const data = await compressRasterImageForUpload(file);

        return {
          source: "file input",
          name: data.name,
          type: data.type,
          data,
          meta,
        };
      })
    );

    return this.uppyInstance.addFiles(filesWithMeta);
  }

  public upload() {
    this.uppyInstance.upload();
  }
}
